import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { MaterialService } from '../../../services/material/material.service';
import { RubroService } from '../../../services/rubro/rubro.service';
import { TipoMaterialService } from '../../../services/tipo-material/tipo-material.service';
import { TarifaService } from '../../../services/tarifa/tarifa.service';
import { Material } from '../../../domain/material.domain';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-material-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    AutoCompleteModule,
    TextareaModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './material-edit.component.html',
  styleUrls: ['./material-edit.component.scss']
})
export class MaterialEditComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private materialService = inject(MaterialService);
  private rubroService = inject(RubroService);
  private tipoMaterialService = inject(TipoMaterialService);
  private tarifaService = inject(TarifaService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private destroy$ = new Subject<void>();

  form: FormGroup;
  loading = signal(false);
  isNew = true;
  materialId: number | null = null;

  // Signals para autocomplete
  rubrosDisponibles = signal<any[]>([]);
  rubrosFiltrados = signal<any[]>([]);
  tiposMaterialDisponibles = signal<any[]>([]);
  tiposMaterialFiltrados = signal<any[]>([]);
  tarifasDisponibles = signal<any[]>([]);
  tarifasFiltradas = signal<any[]>([]);
  frecuencias = [
    { label: 'Diaria', value: 'diaria' },
    { label: 'Semanal', value: 'semanal' },
    { label: 'Mensual', value: 'mensual' },
    { label: 'Anual', value: 'anual' }
  ];

  constructor() {
    this.form = this.fb.group({
      codigo: ['', [Validators.required, Validators.maxLength(255)]],
      nombre: ['', [Validators.required, Validators.maxLength(255)]],
      descripcion: ['', Validators.maxLength(255)],
      frecuencia: ['', Validators.required],
      costoDelSegundo: ['', [Validators.required, Validators.min(0)]],
      duracion: [null as number | null, [Validators.min(0)]],
      rubro: ['', Validators.required],
      tipoMaterial: ['', Validators.required],
      tarifa: ['', Validators.required],
      activo: [true]
    });
  }

  ngOnInit(): void {
    this.cargarRubros();
    this.cargarTiposMaterial();
    this.cargarTarifas();

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'nuevo') {
      this.isNew = false;
      this.materialId = parseInt(idParam, 10);
      this.cargarMaterial();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargarRubros(): void {
    this.rubroService.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const rubros = resp['hydra:member'] || resp.member || resp;
          const rubrosData = rubros.map((r: any) => ({
            id: r.id,
            nombre: r.nombre,
            value: `/api/rubros/${r.id}`
          }));
          this.rubrosDisponibles.set(rubrosData);
          this.rubrosFiltrados.set(rubrosData);
        },
        error: (error: any) => {
          console.error('Error al cargar rubros:', error);
        }
      });
  }

  cargarTiposMaterial(): void {
    this.tipoMaterialService.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const tipos = resp['hydra:member'] || resp.member || resp;
          const tiposData = tipos.map((t: any) => ({
            id: t.id,
            nombre: t.nombre,
            value: `/api/tipo-materials/${t.id}`
          }));
          this.tiposMaterialDisponibles.set(tiposData);
          this.tiposMaterialFiltrados.set(tiposData);
        },
        error: (error: any) => {
          console.error('Error al cargar tipos de material:', error);
        }
      });
  }

  cargarTarifas(): void {
    this.tarifaService.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const tarifas = resp['hydra:member'] || resp.member || resp;
          const tarifasData = tarifas.map((t: any) => ({
            id: t.id,
            nombre: t.nombre,
            value: `/api/tarifas/${t.id}`
          }));
          this.tarifasDisponibles.set(tarifasData);
          this.tarifasFiltradas.set(tarifasData);
        },
        error: (error: any) => {
          console.error('Error al cargar tarifas:', error);
        }
      });
  }

  cargarMaterial(): void {
    if (!this.materialId) return;

    this.loading.set(true);
    this.materialService.getById(this.materialId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (material: Material) => {
          this.loading.set(false);
          const rubroValue = typeof material.rubro === 'string'
            ? material.rubro
            : typeof material.rubro === 'number'
              ? `/api/rubros/${material.rubro}`
              : material.rubro?.id
                ? `/api/rubros/${material.rubro.id}`
                : null;
          const tipoMaterialValue = typeof material.tipoMaterial === 'string'
            ? material.tipoMaterial
            : typeof material.tipoMaterial === 'number'
              ? `/api/tipo-materials/${material.tipoMaterial}`
              : material.tipoMaterial?.id
                ? `/api/tipo-materials/${material.tipoMaterial.id}`
                : null;
          const tarifaValue = typeof material.tarifa === 'string'
            ? material.tarifa
            : typeof material.tarifa === 'number'
              ? `/api/tarifas/${material.tarifa}`
              : material.tarifa?.id
                ? `/api/tarifas/${material.tarifa.id}`
                : null;
          this.form.patchValue({
            codigo: material.codigo,
            nombre: material.nombre,
            descripcion: material.descripcion,
            frecuencia: material.frecuencia,
            costoDelSegundo: material.costoDelSegundo,
            duracion: material.duracion ?? null,
            rubro: rubroValue,
            tipoMaterial: tipoMaterialValue,
            tarifa: tarifaValue,
            activo: material.activo ?? true
          });
        },
        error: (error: any) => {
          this.loading.set(false);
          console.error('Error al cargar material:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo cargar el material',
            life: 3000
          });
        }
      });
  }

  guardar(): void {
    if (!this.form.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor, completa todos los campos requeridos',
        life: 3000
      });
      return;
    }

    this.loading.set(true);
    const formValue = this.form.value;

    if (this.isNew) {
      this.materialService.create(formValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Material creado correctamente',
              life: 3000
            });
            setTimeout(() => this.router.navigate(['/materiales']), 1500);
          },
          error: (error: any) => {
            this.loading.set(false);
            console.error('Error al crear material:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo crear el material',
              life: 3000
            });
          }
        });
    } else {
      this.materialService.update(this.materialId!, formValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Material actualizado correctamente',
              life: 3000
            });
            setTimeout(() => this.router.navigate(['/materiales']), 1500);
          },
          error: (error: any) => {
            this.loading.set(false);
            console.error('Error al actualizar material:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo actualizar el material',
              life: 3000
            });
          }
        });
    }
  }

  cancelar(): void {
    this.router.navigate(['/materiales']);
  }

  filtrarRubros(event: any): void {
    const query = event.query?.toLowerCase() || '';
    const todasLosRubros = this.rubrosDisponibles();
    const rubrosFiltrados = todasLosRubros.filter(rubro => {
      const nombre = rubro.nombre?.toLowerCase() || '';
      return nombre.includes(query);
    });
    this.rubrosFiltrados.set(rubrosFiltrados);
  }

  filtrarTiposMaterial(event: any): void {
    const query = event.query?.toLowerCase() || '';
    const todosLosTipos = this.tiposMaterialDisponibles();
    const tiposFiltrados = todosLosTipos.filter(tipo => {
      const nombre = tipo.nombre?.toLowerCase() || '';
      return nombre.includes(query);
    });
    this.tiposMaterialFiltrados.set(tiposFiltrados);
  }

  filtrarTarifas(event: any): void {
    const query = event.query?.toLowerCase() || '';
    const todasLasTarifas = this.tarifasDisponibles();
    const tarifasFiltradas = todasLasTarifas.filter(tarifa => {
      const nombre = tarifa.nombre?.toLowerCase() || '';
      return nombre.includes(query);
    });
    this.tarifasFiltradas.set(tarifasFiltradas);
  }
}
